import { css } from '@emotion/core'
import { Button, FormLabel, HFlow, Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { resolveName } from 'components/form'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useServerTime } from 'hooks/useServerTime'
import React, { useState } from 'react'
import { useField, useForm } from 'react-final-form'
import { isUndefinedOrNull } from 'util/checks'
import { MetaPath } from 'util/metaPath'

import { ReactComponent as CalendarDrop } from '../../../../../../../images/soap/calendar-drop-outline.svg'
import { safeFormatDumInfo } from './format-preNatalDumSection'
import { PreNatalDumModel } from './model-preNatalDumSection'
import { PreNatalDumForm } from './PreNatalDumForm'

interface PreNatalDumSectionProps {
  meta: MetaPath<LocalDate>
}

export function PreNatalDumSection(props: Readonly<PreNatalDumSectionProps>) {
  const { meta } = props

  const {
    cidadao: { ultimaDum },
  } = useAtendimentoContext()

  const {
    input: { value: dumInput, onChange },
  } = useField<LocalDate | null>(resolveName(meta), { allowNull: true, initialValue: null })
  const { blur } = useForm()

  const isDumRequired = isUndefinedOrNull(ultimaDum)
  const [isEditing, setIsEditing] = useState(isDumRequired)

  const handleSave = ({ dum }: PreNatalDumModel) => {
    setIsEditing(false)
    onChange(dum)
    blur(meta.absolutePath())
  }

  const { getServerTimeNow } = useServerTime()
  const dumInfo = safeFormatDumInfo(dumInput, ultimaDum, getServerTimeNow().getTime())

  const theme = useTheme()
  const styles = createStyles(theme)

  return (
    <VFlow>
      <HFlow hSpacing={0.5} alignItems='center' justifyContent='flex-start'>
        <CalendarDrop />
        <FormLabel label='DUM' required={isDumRequired} />
      </HFlow>
      {!isEditing && (
        <HFlow alignItems='center'>
          <VFlow vSpacing={0.25}>
            <Text fontWeight='bold'>{dumInfo?.value}</Text>
            <Text fontStyle='italic' style={styles.lastUpdate}>
              {dumInfo?.lastUpdate}
            </Text>
          </VFlow>
          <Button kind='primary' size='small' skin='outline' onClick={() => setIsEditing(true)}>
            <Icon icon='penOutline' style={styles.icon} />
            Alterar DUM
          </Button>
        </HFlow>
      )}
      {isEditing && (
        <PreNatalDumForm
          onSubmit={handleSave}
          onCancel={() => setIsEditing(false)}
          initialValues={{ dum: dumInput }}
          required={isDumRequired}
          dataTestId='PreNatalDumSection'
        />
      )}
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  icon: css`
    margin-right: 0.5rem;
  `,
  lastUpdate: css`
    color: ${theme.pallete.gray.c40};
  `,
})
